"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { Search, Check, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../utils";
import { Input } from "../../base/input/input";
import { LISTBOX_DND_MIME, parseListboxDragPayload } from "./listbox";
export function ListboxColumn({ label, count, searchValue, onSearchChange, searchPlaceholder, items, selectedItems, onToggleItem, onDoubleClickItem, renderItem, getItemId, allSelected, onSelectAll, side, filterSlot, onDropItems, selectableCount = items.length, lockedIds, lockedHint }) {
    const { t } = useTranslation();
    const columnRef = React.useRef(null);
    const [isDropTarget, setIsDropTarget] = React.useState(false);
    // Handle Cmd+A / Ctrl+A for select all
    const handleKeyDown = React.useCallback((e)=>{
        if ((e.metaKey || e.ctrlKey) && e.key === "a") {
            e.preventDefault();
            onSelectAll(true);
        }
    }, [
        onSelectAll
    ]);
    const handleItemDragStart = React.useCallback((e, itemId)=>{
        // If the dragged item is part of the selection, drag the whole selection;
        // otherwise drag just this item.
        const ids = selectedItems.has(itemId) ? Array.from(selectedItems) : [
            itemId
        ];
        const payload = {
            side,
            ids
        };
        e.dataTransfer.setData(LISTBOX_DND_MIME, JSON.stringify(payload));
        e.dataTransfer.effectAllowed = "move";
    }, [
        side,
        selectedItems
    ]);
    const handleDragOver = React.useCallback((e)=>{
        if (!onDropItems) return;
        // dataTransfer.getData is empty on dragover in most browsers — use types to
        // detect a listbox-originated drag from the opposite side.
        const hasListboxDrag = Array.from(e.dataTransfer.types).includes(LISTBOX_DND_MIME);
        if (!hasListboxDrag) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (!isDropTarget) setIsDropTarget(true);
    }, [
        onDropItems,
        isDropTarget
    ]);
    const handleDragLeave = React.useCallback((e)=>{
        // Only clear when leaving the column entirely.
        if (columnRef.current && columnRef.current.contains(e.relatedTarget)) {
            return;
        }
        setIsDropTarget(false);
    }, []);
    const handleDrop = React.useCallback((e)=>{
        if (!onDropItems) return;
        const payload = parseListboxDragPayload(e.dataTransfer.getData(LISTBOX_DND_MIME));
        setIsDropTarget(false);
        if (!payload || payload.side === side || payload.ids.length === 0) return;
        e.preventDefault();
        onDropItems(payload.ids);
    }, [
        onDropItems,
        side
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        ref: columnRef,
        className: cn("flex min-w-0 flex-1 flex-col rounded-lg border border-border bg-background transition-colors", isDropTarget && "border-primary ring-2 ring-primary/40"),
        onKeyDown: handleKeyDown,
        onDragOver: handleDragOver,
        onDragLeave: handleDragLeave,
        onDrop: handleDrop,
        tabIndex: -1,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "border-b border-border p-3",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center justify-between",
                    children: [
                        /*#__PURE__*/ _jsxs("h3", {
                            className: "text-sm font-medium",
                            children: [
                                label,
                                " (",
                                count,
                                ")"
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "flex items-center gap-2",
                            children: /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                onClick: ()=>onSelectAll(!allSelected),
                                disabled: selectableCount === 0,
                                className: cn("flex items-center gap-2 text-xs transition-colors cursor-pointer", "hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50", allSelected ? "text-foreground" : "text-muted-foreground"),
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "flex h-4 w-4 items-center justify-center shrink-0",
                                        children: allSelected && /*#__PURE__*/ _jsx(Check, {
                                            className: "h-4 w-4"
                                        })
                                    }),
                                    t("common.selectAll")
                                ]
                            })
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "border-b border-border p-3",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center gap-2",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "relative min-w-0 flex-1",
                            children: [
                                /*#__PURE__*/ _jsx(Search, {
                                    className: "absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    type: "text",
                                    placeholder: searchPlaceholder,
                                    value: searchValue,
                                    onChange: (e)=>onSearchChange(e.target.value),
                                    className: "pl-9"
                                })
                            ]
                        }),
                        filterSlot && /*#__PURE__*/ _jsx("div", {
                            className: "shrink-0",
                            children: filterSlot
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "overflow-y-auto",
                style: {
                    height: "400px"
                },
                children: items.length === 0 ? /*#__PURE__*/ _jsx("div", {
                    className: "flex h-full items-center justify-center p-4 text-center text-sm text-muted-foreground",
                    children: t("common.noItemsFound")
                }) : /*#__PURE__*/ _jsx("div", {
                    children: items.map((item, index)=>{
                        const itemId = getItemId(item);
                        const isSelected = selectedItems.has(itemId);
                        return /*#__PURE__*/ _jsx(ListboxItemComponent, {
                            itemId: itemId,
                            item: item,
                            index: index,
                            isSelected: isSelected,
                            onToggle: (shiftKey)=>onToggleItem(itemId, index, shiftKey),
                            onDoubleClick: ()=>onDoubleClickItem(itemId),
                            onDragStart: handleItemDragStart,
                            renderItem: renderItem,
                            isLocked: lockedIds?.has(itemId) ?? false,
                            lockedHint: lockedHint
                        }, itemId);
                    })
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "border-t border-border p-2 px-3",
                children: /*#__PURE__*/ _jsx("p", {
                    className: "text-xs text-muted-foreground",
                    children: selectedItems.size > 0 ? t("common.itemsSelected", {
                        count: selectedItems.size
                    }) : " "
                })
            })
        ]
    });
}
function ListboxItemComponent({ itemId, item, isSelected, onToggle, onDoubleClick, onDragStart, renderItem, isLocked, lockedHint }) {
    const [isDragging, setIsDragging] = React.useState(false);
    const handleClick = React.useCallback((e)=>{
        onToggle(e.shiftKey);
    }, [
        onToggle
    ]);
    const handleDragStart = React.useCallback((e)=>{
        onDragStart(e, itemId);
        setIsDragging(true);
    }, [
        onDragStart,
        itemId
    ]);
    const handleDragEnd = React.useCallback(()=>{
        setIsDragging(false);
    }, []);
    return /*#__PURE__*/ _jsxs("div", {
        className: cn("group flex items-center gap-3 px-3 py-2.5 transition-all duration-150", isLocked ? "cursor-default" : "cursor-pointer hover:bg-muted/50", isSelected && "bg-muted hover:bg-muted", isDragging && "opacity-50"),
        draggable: !isLocked,
        onDragStart: isLocked ? undefined : handleDragStart,
        onDragEnd: isLocked ? undefined : handleDragEnd,
        onClick: isLocked ? undefined : handleClick,
        onDoubleClick: isLocked ? undefined : onDoubleClick,
        role: "option",
        "aria-selected": isSelected,
        "aria-disabled": isLocked || undefined,
        title: isLocked ? lockedHint : undefined,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex h-4 w-4 items-center justify-center shrink-0",
                children: isLocked ? /*#__PURE__*/ _jsx(Lock, {
                    className: "h-4 w-4 text-muted-foreground"
                }) : isSelected && /*#__PURE__*/ _jsx(Check, {
                    className: "h-4 w-4 text-foreground transition-all"
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: cn("min-w-0 flex-1 wrap-anywhere", isLocked && "text-muted-foreground opacity-60"),
                children: renderItem(item, isSelected)
            })
        ]
    });
}

//# sourceMappingURL=ListboxColumn.js.map